import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { createApiUrl } from '../utils/apiConfig';
import styles from './NewsArticle.module.css';

const NewsArticle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  // Get published articles from localStorage or fallback data
  const [_sampleNews, _setSampleNews] = useState([]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        const response = await axios.get(createApiUrl(`/api/news/articles/${id}`));
        const articleData = response.data;
        setArticle(articleData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching article:', error);
        navigate('/news');
      }
    };

    fetchArticle();
  }, [id, navigate]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const _convertMarkdownToHtml = (markdown) => {
    if (!markdown) return '';
    
    return markdown
      // Headings
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Lists
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      .replace(/^(\d+)\. (.*$)/gim, '<li>$2</li>')
      // Wrap lists in ul/ol
      .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>')
      // Paragraphs
      .replace(/\n\n/g, '</p><p>')
      .replace(/^(?!<[h|u|o])(.*$)/gim, '<p>$1</p>')
      // Clean up empty paragraphs
      .replace(/<p><\/p>/g, '')
      .replace(/<p>(<[h|u|o])/g, '$1')
      .replace(/(<\/[h|u|o]>)<\/p>/g, '$1');
  };

  // Share functions
  const getShareData = () => {
    if (!article) return { url: '', title: '', text: '' };
    
    const currentUrl = window.location.href;
    const shareText = `${article.title}\n\n${article.excerpt}\n\nRead more at KBH Classroom`;
    
    return {
      url: currentUrl,
      title: article.title,
      text: shareText
    };
  };

  const shareOnFacebook = () => {
    const { url } = getShareData();
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const shareOnTwitter = () => {
    const { url, title } = getShareData();
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
  };

  const shareOnLinkedIn = () => {
    const { url } = getShareData();
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(linkedinUrl, '_blank', 'width=600,height=400');
  };

  const shareOnWhatsApp = () => {
    const { text } = getShareData();
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareViaEmail = () => {
    const { url, title, text } = getShareData();
    const emailSubject = `Check out this article: ${title}`;
    const emailBody = `${text}\n\n${url}`;
    const emailUrl = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    window.location.href = emailUrl;
  };

  const copyToClipboard = async () => {
    const { url } = getShareData();
    try {
      await navigator.clipboard.writeText(url);
      setShowCopyNotification(true);
      setTimeout(() => {
        setShowCopyNotification(false);
      }, 3000); // Hide after 3 seconds
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowCopyNotification(true);
      setTimeout(() => {
        setShowCopyNotification(false);
      }, 3000); // Hide after 3 seconds
    }
  };

  const handleShareClick = () => {
    setShowShareModal(true);
  };

  const closeShareModal = () => {
    setShowShareModal(false);
  };

  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <div className={styles.newsArticlePage}>
        <div className="container">
          <div className={styles.loading}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  if (loading) {
    return (
      <div className={styles.newsArticlePage}>
        <div className="container">
          <div className={styles.loading}>Loading article...</div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className={styles.newsArticlePage}>
        <div className="container">
          <div className={styles.error}>Article not found</div>
          <button className={styles.backButton} onClick={() => navigate('/news')}>
            ← Back to News
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.newsArticlePage}>
      <div className={`container ${styles.newsArticleContainer}`}>
        <button className={styles.backButton} onClick={() => navigate('/news')}>
          ← Back to News
        </button>
        
        <article className={styles.articleContent}>
          <header className={styles.articleHeader}>
            <div className={styles.articleCategory}>{article.category}</div>
            <h1 className={styles.articleTitle}>{article.title}</h1>
            <div className={styles.articleMeta}>
              <span className={styles.articleDate}>{formatDate(article.createdAt)}</span>
              <span className={styles.articleAuthor}>By {article.author}</span>
              <span className={styles.articleReadTime}>{article.readTime}</span>
            </div>
          </header>

          <div className={styles.articleImages}>
            {/* Show only cover image if available */}
            {article.coverImage && (
              <div className={styles.articleImage}>
                <img src={article.coverImage} alt={`${article.title} – cover`} />
              </div>
            )}
          </div>

          <div className={styles.articleBody}>
            <p className={styles.articleExcerpt}>{article.excerpt}</p>
            <div 
              className={styles.articleContentText}
              dangerouslySetInnerHTML={{ __html: article.content }}
              onClick={(e) => {
                if (e.target.tagName === 'IMG') {
                  e.preventDefault();
                  handleImageClick(e.target.src);
                }
              }}
            />
          </div>

          <footer className={styles.articleFooter}>
            <div className={styles.articleTags}>
              <span className={styles.tag}>{article.category}</span>
              <span className={styles.tag}>KBH Classroom</span>
              <span className={styles.tag}>Education</span>
            </div>
            <div className={styles.shareButtons}>
              <button className={styles.shareBtn} onClick={handleShareClick}>Share</button>
            </div>
          </footer>
        </article>
        
        <div className={styles.bottomNavigation}>
          <button className={styles.backButton} onClick={() => navigate('/news')}>
            ← Back to News
          </button>
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className={styles.imageModalOverlay} onClick={closeImageModal}>
          <div className={styles.imageModal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeImageModalBtn} onClick={closeImageModal}>×</button>
            <img src={selectedImage} alt="Enlarged view" className={styles.enlargedImage} />
          </div>
        </div>
      )}

      {/* Copy Notification */}
      {showCopyNotification && (
        <div className={styles.copyNotification}>
          <div className={styles.copyNotificationContent}>
            <span>✓ Link copied to clipboard!</span>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className={styles.shareModalOverlay} onClick={closeShareModal}>
          <div className={styles.shareModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.shareModalHeader}>
              <h3>Share this article</h3>
              <button className={styles.closeModalBtn} onClick={closeShareModal}>×</button>
            </div>
            <div className={styles.shareOptions}>
              <button className={styles.shareOption} onClick={shareOnFacebook}>
                <svg className={styles.shareIcon} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </button>
              <button className={styles.shareOption} onClick={shareOnTwitter}>
                <svg className={styles.shareIcon} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
                Twitter
              </button>
              <button className={styles.shareOption} onClick={shareOnLinkedIn}>
                <svg className={styles.shareIcon} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn
              </button>
              <button className={styles.shareOption} onClick={shareOnWhatsApp}>
                <svg className={styles.shareIcon} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                WhatsApp
              </button>
              <button className={styles.shareOption} onClick={shareViaEmail}>
                <svg className={styles.shareIcon} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M0 3v18h24V3H0zm21.518 2L12 12.713 2.482 5h19.036zM2 19V7.183l10 8.104 10-8.104V19H2z"/>
                </svg>
                Email
              </button>
              <button className={styles.shareOption} onClick={copyToClipboard}>
                <svg className={styles.shareIcon} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                </svg>
                Copy Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsArticle; 